import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import PasswordField from "../components/PasswordField";

export default function UpdatePassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // "checking" while the reset link is read, then "ready" or "invalid"
  const [linkState, setLinkState] = useState("checking");
  const navigate = useNavigate();
  const redirectTimer = useRef(null);

  // Opening the emailed link signs the visitor in with a short-lived "recovery" session.
  // Without one (link expired, already used, or the page was opened directly) we can't change anything.
  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setLinkState(data?.session ? "ready" : "invalid");
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || session) setLinkState("ready");
    });

    return () => {
      cancelled = true;
      listener?.subscription?.unsubscribe();
      clearTimeout(redirectTimer.current);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setStatus("");
    setError("");

    if (password !== confirm) {
      setError("The two passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        setError(updateError.message);
      } else {
        setStatus("Password updated successfully. Redirecting to login...");
        redirectTimer.current = setTimeout(() => navigate("/login"), 1500);
      }
    } catch (err) {
      console.error("Updating password failed:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (linkState === "checking") {
    return (
      <div className="auth">
        <div className="auth-card">
          <h2 className="auth-title">Set a new password</h2>
          <p className="auth-sub">Checking your reset link...</p>
        </div>
      </div>
    );
  }

  if (linkState === "invalid") {
    return (
      <div className="auth">
        <div className="auth-card">
          <h2 className="auth-title">This link has expired</h2>
          <p className="auth-sub">
            The password reset link is invalid or has already been used. Request a new one and
            we'll email it to you.
          </p>
          <Link to="/reset-password" className="ui-btn ui-btn--primary ui-btn--block">
            Request a new link
          </Link>
          <p className="auth-foot">
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <div className="auth-card">
        <h2 className="auth-title">Set a new password</h2>
        <p className="auth-sub">Choose a new password for your Aquaviva Foods account.</p>

        <form onSubmit={handleSubmit}>
          {status && (
            <p className="ui-alert ui-alert--ok" role="status">
              {status}
            </p>
          )}
          {error && (
            <p className="ui-alert ui-alert--error" role="alert">
              {error}
            </p>
          )}

          <PasswordField
            id="new-password"
            label="New password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter new password"
            autoComplete="new-password"
            minLength={6}
            hint="At least 6 characters."
          />

          <PasswordField
            id="confirm-password"
            label="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat the new password"
            autoComplete="new-password"
            minLength={6}
          />

          <button
            type="submit"
            className="ui-btn ui-btn--primary ui-btn--block"
            disabled={loading || Boolean(status)}
          >
            {loading ? "Updating..." : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}